import * as React from 'react';
import { ca } from '@cian/analytics';
import { useSelector } from 'react-redux';

import { ECPDEntrySource } from 'shared/types/redux/cpd';
import { selectAnalyticsCPDDealType, selectCPDDraftSource, selectIsCurrentGeoInCPD } from 'shared/selectors/cpd';
import { getCurrentTab } from 'shared/selectors/tabs';
import { getCPDDraftSourceByCurrentTab } from 'shared/utils/cpd';

interface ICPDBannerEventParams {
  action: string;
}

interface ICPDModalEventParams {
  action: string;
  label: string;
}

interface ICPDPrelandingModalEventParams {
  action: string;
  label: string;
}

export function useSendCPDAnalytics() {
  const cpdDraftSource = useSelector(selectCPDDraftSource);
  const currentTab = useSelector(getCurrentTab);
  const isCurrentGeoInCPD = useSelector(selectIsCurrentGeoInCPD);
  const cpdDealType = useSelector(selectAnalyticsCPDDealType);

  const cpdModalPage = React.useMemo(() => {
    return {
      extra: {
        source: cpdDraftSource,
      },
    };
  }, [cpdDraftSource]);

  const cpdBannerPage = React.useMemo(() => {
    return {
      extra: {
        isCpdRegion: isCurrentGeoInCPD,
      },
    };
  }, [isCurrentGeoInCPD]);

  const cpdPrelandingModalPage = React.useMemo(() => {
    return {
      extra: {
        source: cpdDraftSource,
        cpdDealType,
      },
    };
  }, [cpdDraftSource, cpdDealType]);

  const sendCPDModalEvent = React.useCallback(
    (eventParams: ICPDModalEventParams) =>
      ca('eventSite', { name: 'oldevent', category: 'ListingAgents', page: cpdModalPage, ...eventParams }),
    [cpdModalPage],
  );

  const sendCPDBannerEvent = React.useCallback(
    (eventParams: ICPDBannerEventParams) =>
      ca('eventSite', {
        name: 'oldevent',
        category: 'DemandCpd',
        label: getCPDDraftSourceByCurrentTab(currentTab, ECPDEntrySource.Banner),
        page: cpdBannerPage,
        ...eventParams,
      }),
    [cpdBannerPage, currentTab],
  );

  const sendCPDPrelandingModalEvent = React.useCallback(
    (eventParams: ICPDPrelandingModalEventParams) =>
      ca('eventSite', { name: 'oldevent', category: 'DemandCpd', page: cpdPrelandingModalPage, ...eventParams }),
    [cpdPrelandingModalPage],
  );

  return React.useMemo(
    () => ({
      sendCPDModalEvent,
      sendCPDBannerEvent,
      sendCPDPrelandingModalEvent,
    }),
    [sendCPDModalEvent, sendCPDBannerEvent, sendCPDPrelandingModalEvent],
  );
}
